import { rpcClient } from '@/api/rpc/client';
import { xhrPutBlob } from 'src/lib/pricelist-utils';
import {
  waitForBackgroundJob,
  type BackgroundJobWaitResult,
} from '@/api/background-jobs/waitForBackgroundJob';

/**
 * Where a pricelist upload has got to.
 *
 * The two phases are genuinely distinct pieces of waiting, and only the first has
 * a fraction: `uploading` is measured by `XMLHttpRequest`'s upload progress,
 * while `extracting` is a model call running on the server, which reports nothing
 * until it finishes.
 */
export type PricelistUploadPhase =
  { phase: 'uploading'; percent: number } | { phase: 'extracting' };

export interface UploadPricelistInput {
  file: File;
  /** Omitted for an unassigned pricelist. */
  companyId?: string;
  /**
   * Attach the file to this pricelist update case as it is stored (the case
   * page's own upload). The server gives it the case's vendor.
   */
  caseId?: string;
  /**
   * Compare Files' "Compare only" (GEN-2575): extract the file but keep it out
   * of Pricelist Management.
   */
  compareOnly?: boolean;
  /**
   * Called once the server has stored (and, on a case, attached) the file —
   * before any extraction wait, which can run for minutes. The case page
   * refreshes on this so the new file shows up when it exists, not when its
   * tables have been read.
   */
  onStored?: (documentId: string) => void;
}

export interface UploadPricelistResult {
  pdfDocumentId: string;
  /**
   * How the extraction finished, once it has — or null when the file is not
   * extracted at all (an Outlook `.msg` notice is stored and attached, and
   * there is no job to wait on).
   */
  extraction: BackgroundJobWaitResult | null;
}

/**
 * Upload a pricelist file and wait for the server to extract its tables.
 *
 * Deliberately not a hook: this outlives the modal that starts it. It is run by
 * `BackgroundJobsProvider`, so the caller gets phase changes through `onPhase`
 * rather than holding them in component state that dies when the dialog closes.
 *
 * The bytes go straight to blob storage: `getUploadUrl` reserves a presigned
 * staging URL, the file is PUT there raw (an XHR, so upload progress still has
 * a percentage), and `uploadPricelist` then only claims the staged blob — no
 * base64 inflation, no file passing through the API server, and the upload cap
 * is the per-format limit instead of the JSON body ceiling.
 *
 * Extraction does not happen on any of these requests — the claim persists the
 * file and returns a job id, and a Temporal workflow does the slow part. So the
 * wait below is a poll rather than a held-open connection, and the extraction
 * survives this tab going away. What does *not* survive is this function's view
 * of it: a closed tab stops reporting, while the work still finishes.
 */
export async function uploadPricelist(
  { file, companyId, caseId, compareOnly, onStored }: UploadPricelistInput,
  onPhase: (phase: PricelistUploadPhase) => void,
): Promise<UploadPricelistResult> {
  const contentType = file.type || 'application/octet-stream';

  const { uploadKey, uploadUrl } =
    await rpcClient.inventoryManagement.pricelist.getUploadUrl({
      fileName: file.name,
      contentType,
      fileSizeBytes: file.size,
    });

  await xhrPutBlob({
    url: uploadUrl,
    body: file,
    contentType,
    onUploadProgress: (percent) => onPhase({ phase: 'uploading', percent }),
  });
  onPhase({ phase: 'extracting' });

  const { pdfDocumentId, backgroundJobId } =
    await rpcClient.inventoryManagement.pricelist.uploadPricelist({
      uploadKey,
      fileName: file.name,
      ...(companyId === undefined ? {} : { companyId }),
      ...(caseId === undefined ? {} : { caseId }),
      ...(compareOnly === true ? { compareOnly } : {}),
    });
  onStored?.(pdfDocumentId);

  if (backgroundJobId === null) return { pdfDocumentId, extraction: null };

  const extraction = await waitForBackgroundJob(backgroundJobId, () =>
    onPhase({ phase: 'extracting' }),
  );

  return { pdfDocumentId, extraction };
}
